import merge from "lodash.merge";
import styled from "styled-components";
import { Unit } from "../../../../types/globals";
import { ActionButtonStaticProps } from "../ActionButton.types";
import cssObjectForNonQuietState from "./functions/cssObjectForNonQuietState";
import cssObjectForQuietState from "./functions/cssObjectForQuietState";

const EASE_IN_DURATION = 300;
const EASE_OUT_DURATION = 500;
const TRANSITION_TIMING_FUNCTION = "ease-in";

const StyledActionButton = styled.button<
	Required<Omit<ActionButtonStaticProps, "text" | "icon" | "disabled">>
>`
	display: flex;
	flex-direction: ${({ iconPosition }) =>
		iconPosition === "left" ? "row-reverse" : "row"};
	align-items: center;
	justify-content: center;
	gap: ${({ theme }) => theme.space.xs};
	padding: 0
		${({ iconOnly, theme }) => (iconOnly ? theme.space.sm : theme.space.md)};
	width: ${({ iconOnly }) => (iconOnly ? "48px" : "auto")};
	height: 48px;
	border-radius: ${({ theme }) => theme.radii.xs};
	&:not(:disabled) {
		cursor: pointer;
	}

	transition: background-color ${EASE_IN_DURATION}ms
			${TRANSITION_TIMING_FUNCTION},
		color ${EASE_IN_DURATION}ms ${TRANSITION_TIMING_FUNCTION},
		border-color ${EASE_IN_DURATION}ms ${TRANSITION_TIMING_FUNCTION};
	.Typography {
		transition: color ${EASE_IN_DURATION}ms ${TRANSITION_TIMING_FUNCTION};
	}
	.Icon svg {
		transition: fill ${EASE_IN_DURATION}ms ${TRANSITION_TIMING_FUNCTION};
	}

	&:hover {
		transition: background-color ${EASE_OUT_DURATION}ms
				${TRANSITION_TIMING_FUNCTION},
			color ${EASE_OUT_DURATION}ms ${TRANSITION_TIMING_FUNCTION},
			border-color ${EASE_OUT_DURATION}ms ${TRANSITION_TIMING_FUNCTION};
		.Typography {
			transition: color ${EASE_OUT_DURATION}ms ${TRANSITION_TIMING_FUNCTION};
		}
		.Icon svg {
			transition: fill ${EASE_OUT_DURATION}ms ${TRANSITION_TIMING_FUNCTION};
		}
	}

	${({ theme, variant, iconOnly, colorOn }) => {
		const palette = theme.palette;
		const BORDER_WIDTH: Unit = theme.borderWidths.sm;
		const COLOR_FG_DARK_1 = palette.hexForRole("fg_dark_1");
		const COLOR_FG_LIGHT_1 = palette.hexForRole("fg_light_1");
		const COLOR_PRIMARY_ACCENT_300 =
			palette.hexForMandatory("primary_accent_300");
		const COLOR_PRIMARY_ACCENT_100 =
			palette.hexForMandatory("primary_accent_100");
		const COLOR_NEUTRAL_400 = palette.hexForMandatory("neutral_400");
		const COLOR_NEUTRAL_600 = palette.hexForMandatory("neutral_600");

		switch (variant) {
			case "quiet":
				return colorOn === "bg_dark" || colorOn === "bg_dark_variant"
					? merge(cssObjectForQuietState(COLOR_FG_LIGHT_1, "transparent"), {
							"&:hover": cssObjectForQuietState(
								COLOR_FG_DARK_1,
								COLOR_PRIMARY_ACCENT_100
							),
							"&:active": cssObjectForQuietState(
								COLOR_FG_DARK_1,
								COLOR_PRIMARY_ACCENT_300
							),
							"&:disabled": cssObjectForQuietState(
								COLOR_NEUTRAL_600,
								"transparent"
							),
					  })
					: merge(cssObjectForQuietState(COLOR_FG_DARK_1, "transparent"), {
							"&:hover": cssObjectForQuietState(
								COLOR_FG_DARK_1,
								COLOR_PRIMARY_ACCENT_100
							),
							"&:active": cssObjectForQuietState(
								COLOR_FG_DARK_1,
								COLOR_PRIMARY_ACCENT_300
							),
							"&:disabled": cssObjectForQuietState(
								COLOR_NEUTRAL_400,
								"transparent"
							),
					  });
			default: {
				return colorOn === "bg_dark" || colorOn === "bg_dark_variant"
					? merge(
							cssObjectForNonQuietState(
								COLOR_FG_LIGHT_1,
								"transparent",
								COLOR_FG_LIGHT_1,
								BORDER_WIDTH
							),
							{
								padding: `0 ${
									iconOnly
										? theme.space.sm
										: `calc(${theme.space.md} - ${BORDER_WIDTH})`
								}`,
								"&:hover": cssObjectForNonQuietState(
									COLOR_FG_DARK_1,
									COLOR_PRIMARY_ACCENT_100,
									COLOR_PRIMARY_ACCENT_100,
									BORDER_WIDTH
								),
								"&:active": cssObjectForNonQuietState(
									COLOR_FG_DARK_1,
									COLOR_PRIMARY_ACCENT_300,
									COLOR_PRIMARY_ACCENT_300,
									BORDER_WIDTH
								),
								"&:disabled": cssObjectForNonQuietState(
									COLOR_NEUTRAL_600,
									"transparent",
									COLOR_NEUTRAL_600,
									BORDER_WIDTH
								),
							}
					  )
					: merge(
							cssObjectForNonQuietState(
								COLOR_FG_DARK_1,
								"transparent",
								COLOR_FG_DARK_1,
								BORDER_WIDTH
							),
							{
								padding: `0 ${
									iconOnly
										? theme.space.sm
										: `calc(${theme.space.md} - ${BORDER_WIDTH})`
								}`,
								"&:hover": cssObjectForNonQuietState(
									COLOR_FG_DARK_1,
									COLOR_PRIMARY_ACCENT_100,
									COLOR_PRIMARY_ACCENT_100,
									BORDER_WIDTH
								),
								"&:active": cssObjectForNonQuietState(
									COLOR_FG_DARK_1,
									COLOR_PRIMARY_ACCENT_300,
									COLOR_PRIMARY_ACCENT_300,
									BORDER_WIDTH
								),
								"&:disabled": cssObjectForNonQuietState(
									COLOR_NEUTRAL_400,
									"transparent",
									COLOR_NEUTRAL_400,
									BORDER_WIDTH
								),
							}
					  );
			}
		}
	}};
`;

export default StyledActionButton;
